import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocales } from '../../services/referenceService'
import { createBranch, updateBranch } from '../../services/branchService'
import { apiError } from '../../api/apiError'
import type { BranchResponse, Locale } from '../../types'
import './BranchFormModal.css'

interface Props {
  branch?: BranchResponse
  onClose: () => void
  onSaved: (b: BranchResponse) => void
}

export default function BranchFormModal({ branch, onClose, onSaved }: Props) {
  const isEdit = !!branch
  const { t } = useTranslation()

  const [name, setName] = useState(branch?.name ?? '')
  const [address, setAddress] = useState(branch?.address ?? '')
  const [displayName, setDisplayName] = useState(branch?.displayName ?? '')
  const [phone, setPhone] = useState(branch?.phone ?? '')
  const [email, setEmail] = useState(branch?.email ?? '')
  const [muteReportVideo, setMuteReportVideo] = useState(branch?.muteReportVideo ?? false)
  const [localeId, setLocaleId] = useState('')
  const [locales, setLocales] = useState<Locale[]>([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    getLocales().then(setLocales).catch(() => {})
  }, [])

  function validate() {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = t('admin.branch_form.required')
    if (!address.trim()) e.address = t('admin.branch_form.required')
    if (!isEdit && !localeId) e.localeId = t('admin.branch_form.required')
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) e.email = t('admin.branch_form.invalid_email')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const body = {
        name: name.trim(),
        address: address.trim(),
        localeId,
        displayName: displayName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        muteReportVideo,
      }
      const result = isEdit
        ? await updateBranch(branch!.id, body)
        : await createBranch(body)
      onSaved(result)
    } catch (err) {
      const { code } = apiError(err)
      if (code === 'PHONE_INVALID') setErrors({ phone: t('admin.branch_form.invalid_phone') })
      else if (code === 'LOCALE_NOT_FOUND') setErrors({ localeId: t('admin.branch_form.locale_not_found') })
      else if (code === 'BRANCH_ARCHIVED') setErrors({ _: t('admin.branch_form.branch_archived') })
      else setErrors({ _: t('admin.branch_form.save_failed') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{isEdit ? t('admin.branch_form.title_edit') : t('admin.branch_form.title_new')}</h2>
          <button className="modal__close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit}>
          <div className="modal__field">
            <label className="modal__label">{t('admin.branch_form.label_name')}</label>
            <input
              className={`admin-input modal__input${errors.name ? ' admin-input--error' : ''}`}
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: '' })) }}
              disabled={loading}
              autoFocus
            />
            {errors.name && <span className="modal__error">{errors.name}</span>}
          </div>

          <div className="modal__field">
            <label className="modal__label">{t('admin.branch_form.label_address')}</label>
            <input
              className={`admin-input modal__input${errors.address ? ' admin-input--error' : ''}`}
              value={address}
              onChange={(e) => { setAddress(e.target.value); setErrors((p) => ({ ...p, address: '' })) }}
              disabled={loading}
            />
            {errors.address && <span className="modal__error">{errors.address}</span>}
          </div>

          <div className="modal__field">
            <label className="modal__label">{t('admin.branch_form.label_display_name')}</label>
            <input
              className="admin-input modal__input"
              value={displayName}
              placeholder={name}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading}
            />
            <span className="modal__hint">{t('admin.branch_form.display_name_hint')}</span>
          </div>

          <div className="modal__field">
            <label className="modal__label">{t('admin.branch_form.label_phone')}</label>
            <input
              className={`admin-input modal__input${errors.phone ? ' admin-input--error' : ''}`}
              value={phone}
              placeholder="+18005551234"
              type="tel"
              onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: '' })) }}
              disabled={loading}
            />
            {errors.phone && <span className="modal__error">{errors.phone}</span>}
          </div>

          <div className="modal__field">
            <label className="modal__label">{t('admin.branch_form.label_email')}</label>
            <input
              className={`admin-input modal__input${errors.email ? ' admin-input--error' : ''}`}
              value={email}
              type="email"
              onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: '' })) }}
              disabled={loading}
            />
            {errors.email && <span className="modal__error">{errors.email}</span>}
          </div>

          <label className="modal__checkbox">
            <input
              type="checkbox"
              checked={muteReportVideo}
              onChange={(e) => setMuteReportVideo(e.target.checked)}
              disabled={loading}
            />
            <span>
              {t('admin.branch_form.label_mute_video')}
              <span className="modal__hint">{t('admin.branch_form.mute_video_hint')}</span>
            </span>
          </label>

          {!isEdit && (
            <div className="modal__field">
              <label className="modal__label">{t('admin.branch_form.label_locale')}</label>
              <select
                className={`admin-select modal__input${errors.localeId ? ' admin-input--error' : ''}`}
                value={localeId}
                onChange={(e) => { setLocaleId(e.target.value); setErrors((p) => ({ ...p, localeId: '' })) }}
                disabled={loading}
              >
                <option value="">{t('admin.branch_form.select_locale')}</option>
                {locales.map((l) => (
                  <option key={l.id} value={l.id}>{l.code.toUpperCase()}</option>
                ))}
              </select>
              {errors.localeId && <span className="modal__error">{errors.localeId}</span>}
            </div>
          )}

          {errors._ && <p className="modal__error">{errors._}</p>}

          <div className="modal__footer">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={onClose} disabled={loading}>
              {t('admin.branch_form.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
              {loading ? t('admin.branch_form.saving') : isEdit ? t('admin.branch_form.save') : t('admin.branch_form.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
